import { formatDate, parseDateString } from './format';

export function addMonths(date: Date, months: number) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + months);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function subtractMonths(date: Date, months: number) {
  const d = new Date(date);
  d.setMonth(d.getMonth() - months);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function normalizeDate(date: Date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function getFirstDayOfNextMonth() {
  const today = new Date();
  const firstDayNextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);
  firstDayNextMonth.setHours(0, 0, 0, 0);
  return firstDayNextMonth;
}

// Helper function to calculate the first available start date (today + 5 working days, so 6th working day is available)
export function getNextWorkingDay() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let workingDays = 0;
  const currentDate = new Date(today);

  while (workingDays < 5) {
    currentDate.setDate(currentDate.getDate() + 1);
    if (currentDate.getDay() >= 1 && currentDate.getDay() <= 5) {
      workingDays++;
    }
  }

  return currentDate;
}

/** Form values that can be coerced into a `Date` (e.g. dynamic / employment fields). */
export type DateResolvableValue = Date | string | null | undefined;

/**
 * Resolves a form field value to a Date, handling both Date objects and
 * date strings in common formats (used because the employment form stores
 * start_date / end_date as strings while dynamic fields use Date objects).
 */
export function resolveToDate(value: DateResolvableValue) {
  if (!value) return undefined;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
  if (typeof value === 'string' && value.trim()) {
    return (
      parseDateString(value, 'yyyy-MM-dd') ??
      parseDateString(value, 'dd/MM/yyyy HH:mm:ss') ??
      undefined
    );
  }
  return undefined;
}

/** Formats a read-only date field; falls back to the raw string when parsing fails. */
export function formatReadOnlyDateValue(value: DateResolvableValue): string {
  const date = resolveToDate(value);
  if (date) return formatDate(date, 'dd/MM/yyyy');
  if (typeof value === 'string' && value.trim()) return value.trim();
  return '';
}

export function getMonthDifference(from: Date, to: Date): number {
  const yearDiff = to.getFullYear() - from.getFullYear();
  const monthDiff = to.getMonth() - from.getMonth();
  const rawMonths = yearDiff * 12 + monthDiff;

  if (to.getDate() < from.getDate()) {
    return rawMonths - 1;
  }

  return rawMonths;
}

export function getDayDifference(from: Date, to: Date): number {
  const fromNorm = new Date(from);
  const toNorm = new Date(to);

  fromNorm.setHours(0, 0, 0, 0);
  toNorm.setHours(0, 0, 0, 0);

  const fromUtc = Date.UTC(fromNorm.getFullYear(), fromNorm.getMonth(), fromNorm.getDate());
  const toUtc = Date.UTC(toNorm.getFullYear(), toNorm.getMonth(), toNorm.getDate());

  return Math.floor((toUtc - fromUtc) / (1000 * 60 * 60 * 24));
}

export function isSameDay(leftDate: Date, rightDate: Date) {
  return (
    leftDate.getFullYear() === rightDate.getFullYear() &&
    leftDate.getMonth() === rightDate.getMonth() &&
    leftDate.getDate() === rightDate.getDate()
  );
}

export function getAdultAgeDate() {
  const date = new Date();
  const minAge = 18;
  date.setFullYear(date.getFullYear() - minAge);
  return normalizeDate(date);
}

/**
 * Returns a human-readable relative time string for a given date.
 * For recent dates returns "X minutes/hours/days ago", for older dates
 * returns a formatted absolute date string.
 *
 * @param date - The date to format (Date object or ISO string)
 * @returns A relative time string (e.g. "5 minutes ago", "3 days ago",
 *          or "Jan 15, 2025 at 14:30" for dates older than 30 days)
 *
 * @example
 * formatRelativeTime(new Date()) // "just now"
 * formatRelativeTime("2025-05-01T10:00:00Z") // "3 days ago"
 */
export function formatRelativeTime(date: Date | string): string {
  const target = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diffMs = now.getTime() - target.getTime();
  const diffMinutes = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

  return (
    target.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    }) +
    ' at ' +
    target.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })
  );
}
